import { decryptData, encryptData } from "@/features/cryptography";
import { SupabaseDatabase } from "@/features/supabase";
import { store } from "@/store";
import { supabaseApi } from "@/store/services/supabase";
import { dispatchWithRetries } from "@/store/services/supabase/utils";
import { portfolioExportActions } from "@/store/slices/portfolioExport";
import { PortfolioExportIdFactory } from "@/tools/aggregate/portfolio-export/classes/PortfolioExportIdFactory";
import { createPortfolioAssetStream } from "@/tools/aggregate/portfolio-export/classes/PortfolioExportSupabaseTransformStream";
import { PortfolioRegionType } from "@/tools/aggregate/portfolio-export/types";
import { AdaptationType, autoAdaptationTypeValues } from "@/types/adaptation";
import { SecretSalt } from "@/types/cryptography";
import { getAdaptationInputsByAdaptationType } from "@/utils/adaptation";
import { hexToArrayBuffer } from "@/utils/crypto";

type Portfolio = SupabaseDatabase["public"]["Tables"]["portfolios"]["Row"];

/**
 * Data for setting up a portfolio in Supabase.
 */
interface PortfolioDetails {
  name: ArrayBuffer;
  startTime: number;
  encryptedSecret: string;
  groupId?: string;
  isPrivate?: boolean;
  isLargeSite?: boolean;
  metadata?: ArrayBuffer;
  incorporatedHazards: string[];
  regionType?: PortfolioRegionType;
}

type StreamToSupabaseResponse = {
  portfolioId: string;
  autoAdaptationIds: string[];
};

interface PortfolioExportSupabaseResponseSuccess<T> {
  type: "success";
  data: T;
}

type PortfolioExportSupabaseResponseFailure = {
  type: "failure";
  errors: string[];
};

export type PortfolioExportSupabaseResponse<T> =
  | PortfolioExportSupabaseResponseSuccess<T>
  | PortfolioExportSupabaseResponseFailure;

/** Maximum number of lines to send to Supabase at once. */
const MAX_REQUEST_LINES = 100;

type StreamToSupabaseParams = {
  stream: ReadableStream;
  idFactory: PortfolioExportIdFactory;
  details: PortfolioDetails;
  secret: SecretSalt;
  canRunAutoAdaptation: boolean;
  defences?: Record<string, any>;
  anomalyManagementRp?: Record<string, any>;
  regionType?: PortfolioRegionType;
};

/**
 * Pipes a stream through a transformer and spits results at Supabase.
 * @param stream - Stream of CSV file selected for upload.
 * @param idFactory - ID builder for individual lines.
 */
export const streamToSupabase = async ({
  stream,
  idFactory,
  details,
  secret,
  defences,
  anomalyManagementRp,
  regionType,
  canRunAutoAdaptation,
}: StreamToSupabaseParams): Promise<PortfolioExportSupabaseResponse<StreamToSupabaseResponse>> => {
  const supabaseTransformer = createPortfolioAssetStream(idFactory, regionType);
  const reader = stream.pipeThrough(supabaseTransformer).getReader();
  store.dispatch(portfolioExportActions.setNewRunTotalInSupabaseTo(0));

  const addPortfolioResponse = await addPortfolio({ ...details, regionType });

  if (addPortfolioResponse.type === "failure") {
    return addPortfolioResponse;
  }

  const portfolioId = addPortfolioResponse.data.id;

  let autoAdaptationIds: string[] = [];
  let portfolioAutoAdaptations: any[] = [];
  if (canRunAutoAdaptation) {
    const addPortfolioAutoAdaptationsResponse = await addPortfolioAutoAdaptations(portfolioId);
    if (
      addPortfolioAutoAdaptationsResponse.type === "success" &&
      "data" in addPortfolioAutoAdaptationsResponse &&
      addPortfolioAutoAdaptationsResponse?.data
    ) {
      portfolioAutoAdaptations = addPortfolioAutoAdaptationsResponse.data;
      autoAdaptationIds = addPortfolioAutoAdaptationsResponse.data.map((adaptation) => adaptation.id);
    }
  }

  let lines: any[] = [];
  let uploadedTotal = 0;

  const processLines = async (lines: any[]) => {
    return await Promise.all(
      lines.map(async (line) => {
        const data = {
          ...JSON.parse(line.data),
          ...(defences ? { defences } : {}),
          ...(anomalyManagementRp ? { anomaly_management_rp: anomalyManagementRp } : {}),
        };

        return {
          encrypted_id: await encryptData(secret, line.id as string),
          portfolio_id: portfolioId,
          data: await encryptData(secret, JSON.stringify(data)),
        };
      })
    );
  };

  const uploadBatch = async (batch: any[]) => {
    const data = await processLines(batch);
    console.log(`Uploading ${data.length} assets`);

    const res = await addPortfolioAssets(data);

    // Count only successful assets
    if (res.type !== "success") return res;

    store.dispatch(portfolioExportActions.incrementNewRunTotalInSupabaseBy(data.length));
    uploadedTotal += data.length;

    if (canRunAutoAdaptation && res.data) {
      await addPortfolioAssetAdaptationPathways({
        secret,
        portfolioId,
        portfolioAssets: res.data,
        portfolioAdaptationPathways: portfolioAutoAdaptations,
      });
    }

    return res;
  };

  let failure: PortfolioExportSupabaseResponseFailure | null = null;
  let isDone = false;

  while (!isDone) {
    const { done, value } = await reader.read();

    // If we have no more lines, we send the final set of data.
    if (done) {
      isDone = true;

      if (lines.length > 0) {
        const res = await uploadBatch(lines);
        if (res.type === "failure") failure = res;
      }

      continue;
    }

    // Send data only when we reach a set number of lines.
    if (lines.length >= MAX_REQUEST_LINES) {
      const res = await uploadBatch(lines);

      if (res.type === "failure") {
        failure = res;
        // Stop pulling from the file: the portfolio is about to be deleted
        await reader.cancel();
        break;
      }

      lines = [];
    }

    lines.push(value);
  }

  if (failure) {
    await store
      .dispatch(supabaseApi.endpoints.deletePortfolio.initiate(portfolioId))
      .unwrap()
      .then(() => console.log("Portfolio deleted."))
      // Report the upload failure rather than a delete failure that masks it.
      .catch((e) => console.error("Failed to delete partially uploaded portfolio:", e));

    return failure;
  }

  if (portfolioAutoAdaptations.length && uploadedTotal > 0) {
    await seedAdaptationProgressCounts(portfolioAutoAdaptations, uploadedTotal).catch(
      (e) => console.error("Failed to seed adaptation progress counts:", e) // adaptation runs is ongoing but progress is not surfaced while analysis runs
    );
  }

  store.dispatch(supabaseApi.util.invalidateTags([{ type: "Portfolio", id: "List" }]));

  return { type: "success", data: { portfolioId, autoAdaptationIds } };
};

type AddPortfolioReturnType = PortfolioExportSupabaseResponse<Portfolio>;
const addPortfolio = async (
  details: PortfolioDetails & { regionType?: PortfolioRegionType }
): Promise<AddPortfolioReturnType> => {
  const action = supabaseApi.endpoints.addPortfolio.initiate({
    name: details.name,
    groupId: details.groupId,
    encryptedSecret: details.encryptedSecret,
    isPrivate: details.isPrivate,
    isLargeSite: details.isLargeSite,
    metadata: details.metadata,
    incorporatedHazards: details.incorporatedHazards,
    regionType: details.regionType,
  });

  return dispatchWithRetries<Portfolio>({ action, maxRetries: 10, timeout: 5000 })
    .then((data) => {
      return {
        type: "success",
        data: data,
      } as AddPortfolioReturnType;
    })
    .catch((e) => ({
      type: "failure",
      errors: [JSON.stringify(e)],
    }));
};

type Adaptation = SupabaseDatabase["public"]["Tables"]["adaptations"]["Row"];
const addPortfolioAutoAdaptations = async (portfolioId: string) => {
  const action = supabaseApi.endpoints.addAdaptations.initiate(
    autoAdaptationTypeValues.map((adaptation_type) => ({ portfolio_id: portfolioId, adaptation_type }))
  );

  return dispatchWithRetries<null>({ action, maxRetries: 5, timeout: 5000 })
    .then((data) => {
      return {
        type: "success",
        data: data ?? [],
      } as PortfolioExportSupabaseResponse<Adaptation[]>;
    })
    .catch((e) => ({
      type: "failure",
      errors: [JSON.stringify(e)],
    }));
};

// seed adaptation progress (total/pending) count values so they're not null and progress tracker can track it till pending === 0
const seedAdaptationProgressCounts = async (adaptations: { id: string; adaptation_type: string }[], total: number) =>
  await Promise.all(
    adaptations.map((adaptation) => {
      const isBaseline = adaptation.adaptation_type === "baseline";
      const action = supabaseApi.endpoints.updateAdaptationProgressCounts.initiate({
        id: adaptation.id,
        total,
        pending: isBaseline ? 0 : total,
        success: isBaseline ? total : 0,
        error: 0,
      });
      return dispatchWithRetries({ action, maxRetries: 5, timeout: 5000 });
    })
  );

const addPortfolioAssets = async (
  processed: { encrypted_id: ArrayBuffer; portfolio_id: string; data: ArrayBuffer }[]
) => {
  const action = supabaseApi.endpoints.addPortfolioAssets.initiate(processed);

  return dispatchWithRetries<{ id: string; data: string | null; encrypted_id: string }[]>({
    action,
    maxRetries: 10,
    timeout: 5000,
  })
    .then((data) => {
      return { type: "success" as const, data: data };
    })
    .catch((e) => ({ type: "failure" as const, errors: [JSON.stringify(e)] }));
};

const addPortfolioAssetAdaptationPathways = async ({
  secret,
  portfolioId,
  portfolioAssets,
  portfolioAdaptationPathways,
}: {
  secret: SecretSalt;
  portfolioId: string;
  portfolioAssets: { id: string; data: string | null; encrypted_id: string }[];
  portfolioAdaptationPathways: any[];
}) => {
  const adaptationPathways: {
    portfolio_id: string;
    portfolio_asset_id: string;
    adaptation_id: string;
    adaptation_type: AdaptationType;
  }[] = [];

  portfolioAssets.forEach((asset) => {
    portfolioAdaptationPathways.forEach((adaptation) => {
      adaptationPathways.push({
        portfolio_id: portfolioId,
        portfolio_asset_id: asset.id,
        adaptation_id: adaptation.id,
        adaptation_type: adaptation.adaptation_type,
      });
    });
  });

  const action = supabaseApi.endpoints.addAdaptationPathways.initiate(adaptationPathways);

  return dispatchWithRetries<SupabaseDatabase["public"]["Tables"]["adaptation_pathways"]["Row"][]>({
    action,
    maxRetries: 10,
    timeout: 5000,
  })
    .then(async (data) => {
      const pathways = data;
      const actionsPayload = await Promise.all(
        pathways.map(async (pathway) => {
          const relatedAsset = portfolioAssets.find((asset) => asset?.id === pathway?.portfolio_asset_id);
          if (!relatedAsset?.data) {
            return null;
          }

          const decryptedAssetData = await decryptData(secret, hexToArrayBuffer(relatedAsset.data));
          const baselineInputs = JSON.parse(decryptedAssetData);
          const analysisInputData = getAdaptationInputsByAdaptationType({
            baselineInputs,
            adaptationType: pathway.adaptation_type,
          });

          return {
            adaptation_pathway_id: pathway.id,
            encrypted_id: await encryptData(secret, analysisInputData.item_id),
            data: await encryptData(secret, JSON.stringify(analysisInputData)),
            portfolio_asset_id: pathway.portfolio_asset_id,
            adaptation_id: pathway.adaptation_id,
            adaptation_type: pathway.adaptation_type,
            portfolio_id: pathway.portfolio_id,
            is_processed: pathway.adaptation_type === "baseline",
          };
        })
      );

      const actions = supabaseApi.endpoints.addAdaptationPathwayActions.initiate(
        actionsPayload.filter((payload) => payload !== null)
      );
      await dispatchWithRetries({ action: actions, maxRetries: 10, timeout: 5000 });
      return {
        type: "success",
        data: data,
      };
    })
    .catch((e) => ({
      type: "failure",
      errors: [JSON.stringify(e)],
    }));
};

export const createAdaptationsForExistingAssets = async ({
  secret,
  portfolioId,
  portfolioAssets,
}: {
  secret: SecretSalt;
  portfolioId: string;
  portfolioAssets: { id: string; data: string | null; encrypted_id: string }[];
}): Promise<PortfolioExportSupabaseResponse<{ adaptationCount: number }>> => {
  if (portfolioAssets.length === 0) {
    return { type: "failure", errors: ["No successful assets to adapt"] };
  }

  const adaptationsResponse = await addPortfolioAutoAdaptations(portfolioId);
  if (!("data" in adaptationsResponse) || !adaptationsResponse.data?.length) {
    return { type: "failure", errors: ["Failed to create adaptations"] };
  }
  const adaptations = adaptationsResponse.data;

  for (let i = 0; i < portfolioAssets.length; i += MAX_REQUEST_LINES) {
    const batch = portfolioAssets.slice(i, i + MAX_REQUEST_LINES);
    const pathwaysResponse = await addPortfolioAssetAdaptationPathways({
      secret,
      portfolioId,
      portfolioAssets: batch,
      portfolioAdaptationPathways: adaptations,
    });
    if (pathwaysResponse.type === "failure") {
      return { type: "failure", errors: ["Failed to create resilience pathways"] };
    }
  }

  const total = portfolioAssets.length;
  // adaptation only created for successful portfolio assets
  await seedAdaptationProgressCounts(adaptations, total);

  return { type: "success", data: { adaptationCount: total } };
};
